import { Fragment } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons/faArrowLeft';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons/faChevronLeft';
import { faCalendar } from '@fortawesome/pro-regular-svg-icons/faCalendar';
import { useTranslation } from 'react-i18next';
import { type CamelCasedStudentNetworkEvent } from 'StudentNetworkEvent';
import { formatEventDate, getEventTypeName, isOnlineEvent } from '../../utils/eventHelpers';
import { EventDetailSidebar } from './EventDetailSidebar';
import '../../locales/studentNetworkV2/studentNetworkV2-en.json';

type EventDetailProps = {
    event: CamelCasedStudentNetworkEvent;
    onBack: () => void;
    onCategoryFilter?: (categoryKey: string) => void;
    mobileHeaderHidden?: boolean;
};

const LINK_REGEX = /(https?:\/\/[^\s<]+|[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;

// Strip trailing punctuation that's likely sentence-ending, not part of the URL
function cleanUrl(url: string): [string, string] {
    const match = url.match(/^(.+?)[.,;:!?)]+$/);
    if (match) {
        const cleaned = match[1];
        const trailing = url.slice(cleaned.length);
        return [cleaned, trailing];
    }
    return [url, ''];
}

function linkifyText(text: string) {
    const parts = text.split(LINK_REGEX);
    return parts.map(part => {
        if (part.match(/^https?:\/\//)) {
            const [url, trailing] = cleanUrl(part);
            return (
                <Fragment key={part}>
                    <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary">
                        {url}
                    </a>
                    {trailing}
                </Fragment>
            );
        }
        if (part.match(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)) {
            return (
                <a key={part} href={`mailto:${part}`} className="text-primary">
                    {part}
                </a>
            );
        }
        return part;
    });
}

type EventDetailPresentationProps = {
    event: CamelCasedStudentNetworkEvent;
    onBack: () => void;
    onCategoryFilter?: (categoryKey: string) => void;
    mobileHeaderHidden?: boolean;
    /** When true, EventDetailSidebar is excluded (for isolated story rendering) */
    hideSidebar?: boolean;
};

// oxlint-disable-next-line max-lines-per-function -- mobile/desktop layouts with different element ordering
export function EventDetailPresentation({
    event,
    onBack,
    onCategoryFilter,
    mobileHeaderHidden,
    hideSidebar,
}: EventDetailPresentationProps) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'studentNetworkV2.studentNetworkV2.eventDetail' });
    const imageUrl = event.image?.formats?.original?.url;
    const description = (event as Record<string, unknown>).description as string | undefined;
    const online = isOnlineEvent(event.eventType);
    const locationName = event.anonymized ? undefined : event.locationName;

    const tags = (
        <div className="flex flex-wrap gap-2">
            <button
                type="button"
                onClick={() => onCategoryFilter?.(event.eventType)}
                className="cursor-pointer rounded-full border border-transparent bg-slate-grey-100 px-2.5 py-1.5 text-2xs font-medium text-[#282828] hover:border-slate-grey"
            >
                {getEventTypeName(event.eventType)}
            </button>
            <button
                type="button"
                onClick={() => onCategoryFilter?.(online ? 'online' : 'in_person')}
                className="cursor-pointer rounded-full border border-transparent bg-slate-grey-100 px-2.5 py-1.5 text-2xs font-medium text-[#282828] hover:border-slate-grey"
            >
                {online ? 'Online' : 'In-Person'}
            </button>
        </div>
    );

    const image = imageUrl && (
        <div className="mb-6 aspect-[600/338] w-full overflow-hidden rounded-8">
            <img
                src={imageUrl}
                alt=""
                className="h-full w-full object-cover opacity-0 transition-opacity duration-300"
                onLoad={e => {
                    (e.target as HTMLImageElement).classList.remove('opacity-0');
                }}
            />
        </div>
    );

    const detailsSection = description ? (
        <>
            <h2 className="mb-3 text-[20px] font-semibold text-black">{t('details')}</h2>
            <p className="mb-6 whitespace-pre-wrap text-base leading-[22px] text-black">{linkifyText(description)}</p>
        </>
    ) : event.anonymized ? (
        <>
            <h2 className="mb-3 text-[20px] font-semibold text-black">{t('details')}</h2>
            <p className="mb-6 text-base text-slate-grey">{t('detailsAreHidden')}</p>
        </>
    ) : null;

    const sidebar = !hideSidebar && <EventDetailSidebar event={event} />;

    return (
        <div className="p-5 md:p-7.5 md:pb-7.5">
            {/* Mobile header — Sketch: chevron-left coral + centered "EVENT" 18px semibold */}
            {!mobileHeaderHidden && (
                <div className="relative mb-4 flex items-center justify-center lg:hidden">
                    <button
                        type="button"
                        onClick={onBack}
                        className="absolute start-0 text-lg text-primary transition-opacity hover:opacity-70"
                    >
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    <span className="text-lg font-semibold uppercase text-black">{t('event')}</span>
                </div>
            )}

            {/* Desktop back nav — Sketch: 20px regular coral */}
            <button
                type="button"
                onClick={onBack}
                className="mb-6 hidden items-center gap-2 text-[20px] text-primary transition-opacity hover:opacity-70 lg:flex"
            >
                <FontAwesomeIcon icon={faArrowLeft} />
                {t('backToEvents')}
            </button>

            {/* Mobile layout — Sketch: image, title, location, date, details, tags, sidebar */}
            <div className="lg:hidden">
                {image}
                <h1 className="mb-3 text-[25px] font-semibold text-black">{event.title}</h1>
                {locationName && !online && (
                    <div className="mb-3 text-base font-medium text-slate-grey">{locationName}</div>
                )}
                <div className="mb-6 flex items-center gap-1.5">
                    <FontAwesomeIcon icon={faCalendar} className="text-base text-slate-grey" />
                    <span className="text-base font-medium text-slate-grey">{formatEventDate(event)}</span>
                </div>
                {detailsSection}
                <div className="mb-6">{tags}</div>
                {sidebar}
            </div>

            {/* Desktop layout — Sketch: title above, then image+details left / sidebar right */}
            <div className="hidden lg:block">
                <h1 className="mb-6 text-[25px] font-semibold text-black">{event.title}</h1>
                <div className="flex gap-7.5">
                    <div className="min-w-0 flex-1">
                        {image}
                        {detailsSection}
                        {tags}
                    </div>
                    {sidebar && <div className="shrink-0">{sidebar}</div>}
                </div>
            </div>
        </div>
    );
}

export function EventDetail({ event, onBack, onCategoryFilter, mobileHeaderHidden }: EventDetailProps) {
    return (
        <EventDetailPresentation
            event={event}
            onBack={onBack}
            onCategoryFilter={onCategoryFilter}
            mobileHeaderHidden={mobileHeaderHidden}
        />
    );
}
